import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme, useAuth } from '../App'
import { api, User } from '../services/api'

export default function SettingsPage() {
  const navigate = useNavigate()
  const { isDarkMode, toggleDarkMode } = useTheme()
  const { user } = useAuth()

  const [blockedUsers, setBlockedUsers] = useState<User[]>([])
  const [isLoadingBlocked, setIsLoadingBlocked] = useState(false)
  const [showBlockedUsers, setShowBlockedUsers] = useState(false)
  const [showKeyExport, setShowKeyExport] = useState(false)
  const [identityKeyInfo, setIdentityKeyInfo] = useState<{ publicKey: string } | null>(null)

  // Notification settings (stored locally)
  const [notifications, setNotifications] = useState({
    messages: true,
    calls: true,
    sound: true,
  })

  // Privacy settings (stored locally)
  const [privacy, setPrivacy] = useState({
    showOnline: true,
    readReceipts: true,
  })

  useEffect(() => {
    // Load settings from localStorage
    const savedNotifications = localStorage.getItem('lynkstar_notifications')
    if (savedNotifications) {
      setNotifications(JSON.parse(savedNotifications))
    }

    const savedPrivacy = localStorage.getItem('lynkstar_privacy')
    if (savedPrivacy) {
      setPrivacy(JSON.parse(savedPrivacy))
    }

    // Load identity key info
    const identityKey = localStorage.getItem('lynkstar_identity_key')
    if (identityKey) {
      try {
        setIdentityKeyInfo(JSON.parse(identityKey))
      } catch {
        // Ignore
      }
    }
  }, [])

  const saveNotifications = (newSettings: typeof notifications) => {
    setNotifications(newSettings)
    localStorage.setItem('lynkstar_notifications', JSON.stringify(newSettings))
  }

  const savePrivacy = (newSettings: typeof privacy) => {
    setPrivacy(newSettings)
    localStorage.setItem('lynkstar_privacy', JSON.stringify(newSettings))
  }

  const loadBlockedUsers = async () => {
    setIsLoadingBlocked(true)
    try {
      const users = await api.getBlockedUsers()
      setBlockedUsers(users)
    } catch (error) {
      console.error('Failed to load blocked users:', error)
    } finally {
      setIsLoadingBlocked(false)
    }
  }

  const handleUnblockUser = async (userId: string) => {
    try {
      await api.unblockUser(userId)
      setBlockedUsers(prev => prev.filter(u => u.id !== userId))
    } catch (error) {
      console.error('Failed to unblock user:', error)
    }
  }

  const handleExportKeys = () => {
    if (identityKeyInfo) {
      const keyData = JSON.stringify(identityKeyInfo, null, 2)
      const blob = new Blob([keyData], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `lynkstar-keys-${user?.username || 'backup'}.json`
      a.click()
      URL.revokeObjectURL(url)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-dark-bg">
      {/* Header */}
      <header className="app-bar">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-semibold flex-1">Settings</h1>
      </header>

      {/* Settings Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Appearance Section */}
        <section className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
            Appearance
          </h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                <span className="text-gray-700 dark:text-white">Dark Mode</span>
              </div>
              <button
                onClick={toggleDarkMode}
                className={`relative w-12 h-6 rounded-full transition-colors ${isDarkMode ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${isDarkMode ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </section>

        {/* Notifications Section */}
        <section className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
            Notifications
          </h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="text-gray-700 dark:text-white">Message Notifications</span>
              </div>
              <button
                onClick={() => saveNotifications({ ...notifications, messages: !notifications.messages })}
                className={`relative w-12 h-6 rounded-full transition-colors ${notifications.messages ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${notifications.messages ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="text-gray-700 dark:text-white">Call Notifications</span>
              </div>
              <button
                onClick={() => saveNotifications({ ...notifications, calls: !notifications.calls })}
                className={`relative w-12 h-6 rounded-full transition-colors ${notifications.calls ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${notifications.calls ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
                <span className="text-gray-700 dark:text-white">Sound</span>
              </div>
              <button
                onClick={() => saveNotifications({ ...notifications, sound: !notifications.sound })}
                className={`relative w-12 h-6 rounded-full transition-colors ${notifications.sound ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${notifications.sound ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </section>

        {/* Privacy Section */}
        <section className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
            Privacy
          </h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="text-gray-700 dark:text-white">Show Online Status</span>
              </div>
              <button
                onClick={() => savePrivacy({ ...privacy, showOnline: !privacy.showOnline })}
                className={`relative w-12 h-6 rounded-full transition-colors ${privacy.showOnline ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${privacy.showOnline ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-700 dark:text-white">Read Receipts</span>
              </div>
              <button
                onClick={() => savePrivacy({ ...privacy, readReceipts: !privacy.readReceipts })}
                className={`relative w-12 h-6 rounded-full transition-colors ${privacy.readReceipts ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${privacy.readReceipts ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <button
              onClick={() => {
                setShowBlockedUsers(true)
                loadBlockedUsers()
              }}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                <span className="text-gray-700 dark:text-white">Blocked Users</span>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </section>

        {/* Security Section */}
        <section className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
            Security
          </h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <button
              onClick={() => setShowKeyExport(true)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <span className="text-gray-700 dark:text-white">Export Encryption Keys</span>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="px-4 py-3">
              <div className="flex items-center gap-3 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-gray-700 dark:text-white">Signal Protocol</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 ml-8">
                All messages are secured with end-to-end encryption
              </p>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="p-4 pb-8">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
            About
          </h2>
          <div className="bg-gray-50 dark:bg-dark-surface rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <div className="px-4 py-3 flex items-center justify-between">
              <span className="text-gray-700 dark:text-white">Version</span>
              <span className="text-gray-500 dark:text-gray-400">1.0.0</span>
            </div>
            <button className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <span className="text-gray-700 dark:text-white">Privacy Policy</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
            <button className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <span className="text-gray-700 dark:text-white">Terms of Service</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          </div>
        </section>
      </main>

      {/* Blocked Users Modal */}
      {showBlockedUsers && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowBlockedUsers(false)}>
          <div
            className="bg-white dark:bg-dark-surface w-full max-w-lg rounded-t-2xl p-6 max-h-[70vh] overflow-y-auto animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Blocked Users
            </h2>
            {isLoadingBlocked ? (
              <div className="flex items-center justify-center h-32">
                <div className="spinner border-primary border-t-primary-light" />
              </div>
            ) : blockedUsers.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                No blocked users
              </p>
            ) : (
              <div className="space-y-2">
                {blockedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 font-semibold">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-gray-700 dark:text-white">{user.username}</span>
                    </div>
                    <button
                      onClick={() => handleUnblockUser(user.id)}
                      className="px-3 py-1 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                    >
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Key Export Modal */}
      {showKeyExport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowKeyExport(false)}>
          <div
            className="bg-white dark:bg-dark-surface w-full max-w-sm rounded-2xl p-6 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Export Keys
              </h2>
            </div>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Your encryption keys will be exported as a JSON file. Keep this file secure - anyone with access can decrypt your messages.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowKeyExport(false)}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl text-gray-700 dark:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleExportKeys()
                  setShowKeyExport(false)
                }}
                className="flex-1 px-4 py-3 bg-primary hover:bg-primary-dark rounded-xl text-white transition-colors"
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
